/**
 * Simple markdown to HTML converter
 * Converts basic markdown syntax to HTML
 */

// Create a context for all blog assets (images, pdfs, pptx, etc)
const blogAssets = require.context('../data/blog', true, /\.(png|jpe?g|svg|gif|pptx|pdf|zip)$/);

export const markdownToHtml = (markdown, post) => {
  if (!markdown) return '';

  let html = markdown;

  // Remove surrounding quotes if present
  if (html.startsWith('"') && html.endsWith('"')) {
    html = html.substring(1, html.length - 1);
  }

  // Convert images logic
  // Matches ![alt](src "title") or ![alt](src)
  html = html.replace(/!\[([^\]]*)\]\(([^)\s]+)(?:\s+"([^"]+)")?\)/g, (match, alt, src, title) => {
    // If it's an external URL, just return the img tag
    const titleAttr = (title || alt) ? ` title="${title || alt}"` : '';
    if (src.startsWith('http://') || src.startsWith('https://')) {
      return `<img src="${src}" alt="${alt}"${titleAttr} class="img-responsive" />`;
    }

    // Attempt to resolve local image
    if (post && post.path) {
      try {
        // Construct the path relative to the data/blog directory
        // post.path is something like "2025/test3"
        // src is "pirate.jpeg"
        // We look for "./2025/test3/pirate.jpeg"
        const relativePath = `./${post.path}/${src}`;
        const imageSrc = blogAssets(relativePath);
        return `<img src="${imageSrc}" alt="${alt}"${titleAttr} class="img-responsive" />`;
      } catch (error) {
        console.warn(`Could not load image: ${src} for post: ${post.id}`, error);
        return `<img src="${src}" alt="${alt} (Image not found)"${titleAttr} class="img-responsive" />`;
      }
    }

    return `<img src="${src}" alt="${alt}"${titleAttr} class="img-responsive" />`;
  });

  // Convert headers
  html = html.replace(/^#### (.*$)/gim, '<h4>$1</h4>');
  html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
  html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
  html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');

  // Convert bold and italic
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');

  // Convert inline code
  html = html.replace(/`(.*?)`/g, '<code>$1</code>');

  // Convert code blocks
  html = html.replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>');

  // Convert links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, text, href) => {
    // Check if it's an external link
    if (href.startsWith('http://') || href.startsWith('https://')) {
      return `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`;
    }

    // Check if it's an internal link
    if (href.startsWith('/') || href.startsWith('#')) {
      return `<a href="${href}">${text}</a>`;
    }

    // Attempt to resolve local asset
    if (post && post.path) {
      try {
        const relativePath = `./${post.path}/${href}`;
        const assetSrc = blogAssets(relativePath);
        // It's a local file, let's treat it as a download/open link
        return `<a href="${assetSrc}" target="_blank" rel="noopener noreferrer">${text}</a>`;
      } catch (error) {
        // Fallback if not found in context (might be a relative link to another page or anchor)
        // If it starts with /, treat as internal router link maybe? Or just leave as is.
        // For now, let's just return the link as is if asset lookup fails
        return `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`;
      }
    }
    return `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`;
  });

  // Convert unordered lists
  html = html.replace(/^[\s]*[-*+] (.*$)/gim, '<li-ul>$1</li-ul>');
  html = html.replace(/((?:<li-ul>[\s\S]*?<\/li-ul>\s*)+)/gim, '<ul>$1</ul>');

  // Convert ordered lists
  html = html.replace(/^[\s]*\d+\. (.*$)/gim, '<li-ol>$1</li-ol>');
  html = html.replace(/((?:<li-ol>[\s\S]*?<\/li-ol>\s*)+)/gim, '<ol>$1</ol>');

  // Restore li tags
  html = html.replace(/<li-ul>/g, '<li>').replace(/<\/li-ul>/g, '</li>');
  html = html.replace(/<li-ol>/g, '<li>').replace(/<\/li-ol>/g, '</li>');

  // Convert horizontal rules
  html = html.replace(/^---$/gim, '<hr>');

  // Convert tables
  html = html.replace(/^\|(.+)\|\s*\n\|([-:| ]+)\|\s*\n((?:\|.*\|\s*(?:\n|$))+)/gm, (match, header, separator, body) => {
    const headers = header.split('|').map(h => h.trim()).filter(h => h.length > 0 || h === '');
    // If first cell is empty but matched by split (e.g. "| | header |"), clean up might be needed depending on split behavior
    // Better strategy for specific pipe format in the blog:
    // The blog uses "| | CO2 (kg) | % |" which has leading and trailing pipes. 
    // split('|') on "| a | b |" gives ["", " a ", " b ", ""]

    // Helper to parse row
    const parseRow = (row) => {
      // Remove leading and trailing pipes if they exist, but keep empty cells if they are between pipes
      const cleanRow = row.trim().replace(/^\||\|$/g, '');
      return cleanRow.split('|').map(cell => cell.trim());
    };

    const headerCells = parseRow(header);
    const bodyRows = body.trim().split('\n').map(row => parseRow(row));

    let tableHtml = '<table class="table table-bordered table-striped">';

    // Header
    tableHtml += '<thead><tr>';
    headerCells.forEach(cell => {
      tableHtml += `<th>${cell}</th>`;
    });
    tableHtml += '</tr></thead>';

    // Body
    tableHtml += '<tbody>';
    bodyRows.forEach(row => {
      tableHtml += '<tr>';
      row.forEach(cell => {
        tableHtml += `<td>${cell}</td>`;
      });
      tableHtml += '</tr>';
    });
    tableHtml += '</tbody></table>';

    return tableHtml;
  });

  // Wrap normal text in paragraphs
  // Matches consecutive lines that don't start with block tags
  // Excludes: headers, lists, pre, hr, li, img, blockquote, table
  html = html.replace(/(?:^[ \t]*(?!<(?:h[1-6]|ul|ol|pre|hr|li|img|blockquote|p|table)\b).*\S.*$(?:\n|$))+/gm, (match) => {
    let content = match.trim();
    // Remove surrounding quotes if the text block is wrapped in them
    if (content.startsWith('"') && content.endsWith('"')) {
      content = content.slice(1, -1);
    }
    return `<p>${content}</p>\n`;
  });

  // Convert line breaks
  html = html.replace(/\n/g, '<br>');

  // Clean up multiple consecutive <br> tags
  html = html.replace(/(<br>){2,}/g, '<br>');

  // Clean up <br> tags before block elements
  html = html.replace(/(?:<br>)+(<h[1-6]>)/g, '$1');
  html = html.replace(/(?:<br>)+(<ul>)/g, '$1');
  html = html.replace(/(?:<br>)+(<ol>)/g, '$1');
  html = html.replace(/(?:<br>)+(<pre>)/g, '$1');
  html = html.replace(/(?:<br>)+(<hr>)/g, '$1');
  html = html.replace(/(?:<br>)+(<p>)/g, '$1');
  html = html.replace(/(?:<br>)+(<table)/g, '$1');

  // Clean up <br> tags after block elements
  html = html.replace(/(<\/h[1-6]>)(?:<br>)+/g, '$1');
  html = html.replace(/(<\/ul>)(?:<br>)+/g, '$1');
  html = html.replace(/(<\/ol>)(?:<br>)+/g, '$1');
  html = html.replace(/(<\/pre>)(?:<br>)+/g, '$1');
  html = html.replace(/(<hr>)(?:<br>)+/g, '$1');
  html = html.replace(/(<\/li>)(?:<br>)+/g, '$1');
  html = html.replace(/(<\/p>)(?:<br>)+/g, '$1');
  html = html.replace(/(<\/table>)(?:<br>)+/g, '$1');

  return html;
};

/**
 * Load markdown content from a file path
 * Dynamically imports the markdown file based on the post path
 */
export const loadMarkdownContent = async (post) => {
  if (!post) return '';

  try {
    // Dynamically import the markdown file based on the post path and filename (id)
    // We assume all files are .md as per requirement
    const markdownModule = await import(`../data/blog/${post.path}/${post.id}.md`);

    let content = markdownModule.default || '';

    // If the content is a URL path (handled by file-loader for .md files), fetch the actual text
    if (typeof content === 'string' && !content.includes('\n') && (content.startsWith('/static/') || content.endsWith('.md'))) {
      try {
        const response = await fetch(content);
        if (response.ok) {
          content = await response.text();
        }
      } catch (error) {
        console.error('Error fetching markdown content:', error);
      }
    }

    return content;
  } catch (error) {
    console.error('Error loading markdown file:', error);

    // Fallback to placeholder content if file doesn't exist
    return `
# ${post.title}

*Date: ${new Date(post.date).toLocaleDateString()}*

## Introduction

${post.excerpt}

## Content

This blog post content could not be loaded. The markdown file may not exist or there was an error loading it.

## Tags

${post.tags.map(tag => `#${tag}`).join(' ')}

---

*Thanks for reading! Check back for more blog posts.*
    `;
  }
};
